import { useState } from 'react'
import { Line, LineChart, ResponsiveContainer, YAxis } from 'recharts'
import MetricChart, { type SeverityMarker } from './MetricChart'
import { METRICS, type ChartPoint, type MetricKey } from './types'

/**
 * Small-multiple sparklines — one compact trend per headline metric, sharing
 * the deep-dive series buffer. Clicking a tile expands it to the full
 * MetricChart (severity ReferenceLines, multi-series percentiles); click
 * again to collapse back to the grid.
 */
export default function SparklineGrid({ data, severityMarkers }: {
  data: ChartPoint[]
  severityMarkers: SeverityMarker[]
}) {
  const [expanded, setExpanded] = useState<MetricKey | null>(null)

  if (expanded) {
    const def = METRICS.find((m) => m.key === expanded)
    return (
      <div style={{ border: '0.5px solid var(--bd-1)', borderRadius: 'var(--radius)', padding: '10px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 10, fontWeight: 600, color: def?.color ?? 'var(--t-1)' }}>{def?.label}</span>
          <span
            onClick={() => setExpanded(null)}
            style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--t-2)', cursor: 'pointer', userSelect: 'none' }}
          >
            ✕ collapse
          </span>
        </div>
        <MetricChart data={data} metricKey={expanded} height={180} severityMarkers={severityMarkers} />
      </div>
    )
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
      {METRICS.map((m) => {
        const latest = [...data].reverse().find((pt) => typeof pt[m.key] === 'number')
        const latestVal = latest ? (latest[m.key] as number) : null
        return (
          <div
            key={m.key}
            onClick={() => setExpanded(m.key)}
            title="Click to expand"
            style={{
              border: '0.5px solid var(--bd-1)', borderRadius: 'var(--radius)',
              padding: '8px 10px 2px', cursor: 'pointer',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = m.color }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--bd-1)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10 }}>
              <span style={{ color: m.color, fontWeight: 600 }}>{m.label}</span>
              <span style={{ color: 'var(--t-1)', fontWeight: 600 }}>
                {latestVal == null ? '—' : m.axis === 'ratio' ? latestVal.toFixed(2) : Math.round(latestVal * 10) / 10}
              </span>
            </div>
            {data.length ? (
              <ResponsiveContainer width="100%" height={44}>
                <LineChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                  <YAxis hide domain={m.axis === 'ratio' ? [0, 'auto'] : ['auto', 'auto']} />
                  <Line
                    dataKey={m.key}
                    stroke={m.color}
                    strokeWidth={1.25}
                    dot={false}
                    isAnimationActive={false}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--t-2)', fontSize: 10 }}>
                no data
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
